import type { OrchestrationThreadActivity, TurnId } from "@afar-research/contracts";

import { compareOrchestrationActivitiesByOrder } from "./orchestrationActivityOrder.ts";

export type AgentTaskStepStatus = "pending" | "inProgress" | "completed";

export interface AgentTaskListStep {
  readonly id: string;
  readonly step: string;
  readonly status: AgentTaskStepStatus;
  readonly startedAt?: string;
  readonly completedAt?: string;
  readonly durationMs?: number;
}

export interface AgentTaskListState {
  readonly instanceId: string;
  readonly createdAt: string;
  readonly turnId: TurnId | null;
  readonly explanation?: string | null;
  readonly completedCount: number;
  readonly totalCount: number;
  readonly currentStepIndex: number | null;
  readonly steps: ReadonlyArray<AgentTaskListStep>;
}

function taskListInstanceId(activity: OrchestrationThreadActivity): string {
  return activity.turnId ? `turn:${activity.turnId}` : `event:${activity.id}`;
}

interface NormalizedStep {
  readonly id: string;
  readonly step: string;
  readonly status: AgentTaskStepStatus;
}

interface StepTransitionHistory {
  previousStatus: AgentTaskStepStatus | undefined;
  startedAt: string | undefined;
  completedAt: string | undefined;
  durationMs: number | undefined;
}

function parseTaskSteps(payload: unknown): NormalizedStep[] | null {
  if (!payload || typeof payload !== "object") return null;
  const rawPlan = (payload as Record<string, unknown>).plan;
  if (!Array.isArray(rawPlan)) return null;

  const occurrences = new Map<string, number>();
  const steps: NormalizedStep[] = [];
  for (const entry of rawPlan) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    if (typeof record.step !== "string" || record.step.trim().length === 0) continue;
    const step = record.step.trim();
    const occurrence = (occurrences.get(step) ?? 0) + 1;
    occurrences.set(step, occurrence);
    steps.push({
      id: `${step}:${occurrence}`,
      step,
      status:
        record.status === "completed" || record.status === "inProgress" ? record.status : "pending",
    });
  }
  return steps;
}

function uniqueTaskListActivities(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): OrchestrationThreadActivity[] {
  const byEventId = new Map<string, OrchestrationThreadActivity>();
  for (const activity of activities) {
    if (activity.kind === "turn.plan.updated" && !byEventId.has(activity.id)) {
      byEventId.set(activity.id, activity);
    }
  }
  return [...byEventId.values()].toSorted(compareOrchestrationActivitiesByOrder);
}

function transitionDurationMs(startedAt: string, completedAt: string): number | undefined {
  const start = Date.parse(startedAt);
  const end = Date.parse(completedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return undefined;
  return end - start;
}

function projectTransitionHistory(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): Map<string, StepTransitionHistory> {
  const histories = new Map<string, StepTransitionHistory>();

  for (const activity of activities) {
    const steps = parseTaskSteps(activity.payload);
    if (!steps) continue;
    for (const step of steps) {
      const history = histories.get(step.id) ?? {
        previousStatus: undefined,
        startedAt: undefined,
        completedAt: undefined,
        durationMs: undefined,
      };
      if (history.previousStatus === step.status) continue;

      if (step.status === "pending") {
        history.startedAt = undefined;
        history.completedAt = undefined;
        history.durationMs = undefined;
      } else if (step.status === "inProgress") {
        history.startedAt = activity.createdAt;
        history.completedAt = undefined;
        history.durationMs = undefined;
      } else {
        history.completedAt = activity.createdAt;
        history.durationMs = history.startedAt
          ? transitionDurationMs(history.startedAt, activity.createdAt)
          : undefined;
      }
      history.previousStatus = step.status;
      histories.set(step.id, history);
    }
  }

  return histories;
}

/**
 * Project the latest provider-native Agent Task List from persisted activities.
 *
 * The projection is input-order independent, ignores duplicate event ids, and
 * derives transition timing exclusively from persisted provider timestamps.
 */
function projectAgentTaskListState(
  relatedActivities: ReadonlyArray<OrchestrationThreadActivity>,
  latest: OrchestrationThreadActivity,
): AgentTaskListState | null {
  const latestSteps = parseTaskSteps(latest.payload);
  if (!latestSteps || latestSteps.length === 0) return null;

  const histories = projectTransitionHistory(relatedActivities);
  const steps = latestSteps.map<AgentTaskListStep>((step) => {
    const history = histories.get(step.id);
    return {
      ...step,
      ...(history?.startedAt ? { startedAt: history.startedAt } : {}),
      ...(history?.completedAt ? { completedAt: history.completedAt } : {}),
      ...(history?.durationMs !== undefined ? { durationMs: history.durationMs } : {}),
    };
  });
  const activeStepIndex = steps.findIndex((step) => step.status === "inProgress");
  const pendingStepIndex = steps.findIndex((step) => step.status === "pending");
  const payload = latest.payload as Record<string, unknown>;
  const explanation =
    typeof payload.explanation === "string" || payload.explanation === null
      ? payload.explanation
      : undefined;

  return {
    instanceId: taskListInstanceId(latest),
    createdAt: latest.createdAt,
    turnId: latest.turnId,
    ...(explanation !== undefined ? { explanation } : {}),
    completedCount: steps.filter((step) => step.status === "completed").length,
    totalCount: steps.length,
    currentStepIndex:
      activeStepIndex >= 0 ? activeStepIndex : pendingStepIndex >= 0 ? pendingStepIndex : null,
    steps,
  };
}

/** Project every provider-native task-list atom, ordered by its first update. */
export function deriveAgentTaskListStates(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): AgentTaskListState[] {
  const ordered = uniqueTaskListActivities(activities);
  const activitiesByInstance = new Map<string, OrchestrationThreadActivity[]>();

  for (const activity of ordered) {
    const instanceId = taskListInstanceId(activity);
    const related = activitiesByInstance.get(instanceId);
    if (related) {
      related.push(activity);
    } else {
      activitiesByInstance.set(instanceId, [activity]);
    }
  }

  return [...activitiesByInstance.values()].flatMap((relatedActivities) => {
    const latest = relatedActivities.at(-1);
    if (!latest) return [];
    const state = projectAgentTaskListState(relatedActivities, latest);
    return state ? [state] : [];
  });
}

export function deriveAgentTaskListState(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
  latestTurnId: TurnId | undefined,
): AgentTaskListState | null {
  const ordered = uniqueTaskListActivities(activities);
  const matchingLatestTurn = latestTurnId
    ? ordered.findLast((activity) => activity.turnId === latestTurnId)
    : undefined;
  const latest = matchingLatestTurn ?? ordered.at(-1);
  if (!latest) return null;

  return projectAgentTaskListState(
    ordered.filter((activity) => activity.turnId === latest.turnId),
    latest,
  );
}
